import { ChangeDetectionStrategy, Component, inject, input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

import { AbstractDateInputComponent } from '@connect-services/client/components/input/date/abstract-date-input.component';
import { InformationSchemaTableExtended } from '@connect-services/client/sdk/api';
import { AttributeValidationService } from '@connect-services/client/services/attribute-validation.service';
import { CustomDateAdapterService } from '@connect-services/client/services/custom-date-adapter.service';
import { ADAPTER_DATE_FORMATS, TranslateService } from '@connect-services/client/services/translate.service';
import { EntityTableDateCellContext } from '@connect-services/src/app/components/entity-table-date-cell/entity-table-date-cell.component';
import { ButtonComponent } from '@connect-services/src/lib/components/button/button.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, RessourcesModule],
    providers: [
        { deps: [TranslateService], provide: DateAdapter, useClass: CustomDateAdapterService },
        { provide: MAT_DATE_FORMATS, useValue: ADAPTER_DATE_FORMATS },
    ],
    selector: 'app-entity-table-date-hour-cell',
    standalone: true,
    styleUrl: './entity-table-date-hour-cell.component.scss',
    templateUrl: './entity-table-date-hour-cell.component.html',
})
export class EntityTableDateHourCellComponent
    extends AbstractDateInputComponent<Date, InformationSchemaTableExtended>
    implements OnInit
{
    public readonly context = input<EntityTableDateCellContext>(EntityTableDateCellContext.TABLE);

    protected readonly dateControl = new FormControl<Date | null>(null);
    protected readonly EntityTableDateCellContext = EntityTableDateCellContext;
    protected readonly timeControl = new FormControl<string>('00:00');

    private readonly attributeValidationService = inject(AttributeValidationService);
    private readonly translateService = inject(TranslateService);

    public constructor() {
        super();
        const originalFormat = this.dateAdapter.format.bind(this.dateAdapter);
        const originalParse = this.dateAdapter.parse.bind(this.dateAdapter);

        this.dateFormatPlaceholder = this.translateService.getDateHourFormatLabel();
        this.dateAdapter.format = (date: Date, displayFormat: string): string =>
            this.initDateAdapterFormater(date, displayFormat, originalFormat);
        this.dateAdapter.parse = (value: string, parseFormat: string): Date | null =>
            this.initDateAdapterParser(value, parseFormat, originalParse);
    }

    public handleBlur(): void {
        this.dateControl.markAsTouched();
        this.updateCombinedControl();
        this.updateErrorsAndValidity();
        this.emitCombinedDateTime();
    }

    public ngOnInit(): void {
        this.initValue();

        this.updateErrorsAndValidity();
    }

    public onDateChange(event: MatDatepickerInputEvent<Date>): void {
        this.dateControl.setValue(event.value);
        this.updateCombinedControl();
        this.updateErrorsAndValidity();
        this.emitCombinedDateTime();
    }

    public onTimeInputChange(): void {
        this.updateCombinedControl();
        this.updateErrorsAndValidity();
        this.emitCombinedDateTime();
    }

    private emitCombinedDateTime(): void {
        if (this.errorMessages().length) {
            return;
        }

        const combinedValue = this.control.value;

        if (!combinedValue) {
            this.valueChange.emit(null);
            this.onBlur.emit(null);

            return;
        }

        const isoValue = combinedValue.toISOString();
        this.valueChange.emit(isoValue);
        this.onBlur.emit(isoValue);
    }

    private initDateAdapterFormater(
        date: Date,
        displayFormat: string,
        originalFormat: (date: unknown, displayFormat: string) => string,
    ): string {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            return '';
        }
        const baseDateStr = originalFormat(date, displayFormat);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${baseDateStr} ${hours}:${minutes}`;
    }

    private initDateAdapterParser(
        value: string,
        parseFormat: string,
        originalParse: (date: unknown, parseFormat: string) => Date,
    ): Date | null {
        if (typeof value === 'string' && value.trim()) {
            const parts = value.trim().split(' ');
            const datePart = parts[0];
            const timePart = parts[1];

            const parsedDate = originalParse(datePart, parseFormat);

            if (parsedDate && !isNaN(parsedDate.getTime())) {
                if (timePart && /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(timePart)) {
                    const [hours, minutes] = timePart.split(':');
                    parsedDate.setHours(parseInt(hours, 10));
                    parsedDate.setMinutes(parseInt(minutes, 10));

                    this.timeControl.setValue(timePart, { emitEvent: false });
                }

                return parsedDate;
            }
        }

        return originalParse(value, parseFormat);
    }

    private initValue(): void {
        const baseValue = this.value();
        const dateObj = baseValue ? new Date(baseValue as string) : null;

        const baseControl = this.attributeValidationService.createControl(dateObj, this.info());

        this.control.setValidators(baseControl.validator);
        this.control.setValue(dateObj, { emitEvent: false });

        this.dateControl.setValue(dateObj, { emitEvent: false });

        if (dateObj) {
            const hours = String(dateObj.getHours()).padStart(2, '0');
            const minutes = String(dateObj.getMinutes()).padStart(2, '0');
            this.timeControl.setValue(`${hours}:${minutes}`, { emitEvent: false });
        } else {
            this.timeControl.setValue('00:00', { emitEvent: false });
        }
    }

    private updateCombinedControl(): void {
        const date = this.dateControl.value;
        const time = this.timeControl.value;

        if (!date) {
            this.control.setValue(null, { emitEvent: false });

            return;
        }

        const combined = new Date(date);
        if (time) {
            const [hours, minutes] = time.split(':');
            combined.setHours(parseInt(hours, 10));
            combined.setMinutes(parseInt(minutes, 10));
            combined.setSeconds(0);
        }

        this.control.setValue(combined, { emitEvent: false });
    }

    private updateErrorsAndValidity(): void {
        this.control.updateValueAndValidity({ emitEvent: false });
        this.timeControl.updateValueAndValidity({ emitEvent: false });

        const errors = [
            ...this.attributeValidationService.getErrorMessages(this.control),
            ...this.attributeValidationService.getErrorMessages(this.timeControl),
        ];
        this.errorMessages.set(errors);
    }
}
